import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Calculator, FileText, Save, Eye, Lock, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const TEMPLATE_META = {
  attivazione: { label: "Benvenuto / Attivazione account", Icon: Mail },
  reset: { label: "Reimpostazione password", Icon: Lock },
};

function ReferenceEmailsCard() {
  const [ufficio, setUfficio] = useState("");
  const [giustificativi, setGiustificativi] = useState("");
  const [contabilita, setContabilita] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/config/reference-emails");
      setUfficio(data.ufficio_personale || "");
      setGiustificativi(data.spazio_giustificativi || "");
      setContabilita(data.contabilita || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invalid = (v) => v.trim() !== "" && !EMAIL_RE.test(v.trim());

  const save = async () => {
    if (invalid(ufficio) || invalid(giustificativi) || invalid(contabilita)) { toast.error("Controlla il formato degli indirizzi e-mail"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config/reference-emails", {
        ufficio_personale: ufficio.trim(), spazio_giustificativi: giustificativi.trim(), contabilita: contabilita.trim(),
      });
      setUfficio(data.ufficio_personale || "");
      setGiustificativi(data.spazio_giustificativi || "");
      setContabilita(data.contabilita || "");
      toast.success("Email di riferimento salvate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm" data-testid="config-reference-emails">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <Mail className="h-4 w-4 text-primary" />Email di riferimento
        </CardTitle>
        <p className="text-sm text-slate-500">Indirizzi salvati come configurazione, riutilizzabili in futuro da altre funzioni di Orbit. Al momento non viene inviato nulla automaticamente.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Calculator className="h-3.5 w-3.5 text-slate-400" />Email Contabilità</Label>
          <Input data-testid="config-email-contabilita" type="email" value={contabilita} onChange={(e) => setContabilita(e.target.value)}
            placeholder="contabilita@grupposilvabella.it" className={invalid(contabilita) ? "border-destructive" : ""} />
          {invalid(contabilita) && <p className="text-xs text-destructive">Formato e-mail non valido</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />Email Ufficio personale</Label>
          <Input data-testid="config-email-ufficio" type="email" value={ufficio} onChange={(e) => setUfficio(e.target.value)}
            placeholder="ufficio@grupposilvabella.it" className={invalid(ufficio) ? "border-destructive" : ""} />
          {invalid(ufficio) && <p className="text-xs text-destructive">Formato e-mail non valido</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-slate-400" />Email Spazio Giustificativi</Label>
          <Input data-testid="config-email-giustificativi" type="email" value={giustificativi} onChange={(e) => setGiustificativi(e.target.value)}
            placeholder="giustificativi@grupposilvabella.it" className={invalid(giustificativi) ? "border-destructive" : ""} />
          {invalid(giustificativi) && <p className="text-xs text-destructive">Formato e-mail non valido</p>}
        </div>
        <Button onClick={save} disabled={saving} data-testid="config-emails-save" className="rounded-lg font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Salva
        </Button>
      </CardContent>
    </Card>
  );
}

function TemplatesCard() {
  const [kind, setKind] = useState("attivazione");
  const [tpl, setTpl] = useState(null); // {attivazione:{oggetto,titolo,testo,firma,protected}, reset:{...}}
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // {subject, html}
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/config/email-templates");
      setTpl(data.templates);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (f, v) => setTpl((p) => ({ ...p, [kind]: { ...p[kind], [f]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      for (const k of ["attivazione", "reset"]) {
        body[k] = { oggetto: tpl[k].oggetto || "", titolo: tpl[k].titolo || "", testo: tpl[k].testo || "", firma: tpl[k].firma || "" };
      }
      await api.put("/admin/config/email-templates", body);
      toast.success("Template salvati");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const cur = tpl[kind];
      const { data } = await api.post("/admin/config/email-templates/preview", {
        kind, oggetto: cur.oggetto || "", titolo: cur.titolo || "", testo: cur.testo || "", firma: cur.firma || "",
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPreviewing(false); }
  };

  if (!tpl) return <Card className="rounded-xl"><CardContent className="p-6 text-center text-slate-500 text-sm">Caricamento template…</CardContent></Card>;

  const cur = tpl[kind];
  const prot = cur.protected || {};

  return (
    <Card className="rounded-xl border-slate-200 shadow-sm" data-testid="config-templates">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />Template email
        </CardTitle>
        <p className="text-sm text-slate-500">Personalizza oggetto, intestazione, testo e firma. Le parti tecniche (link/token, pulsante, scadenza, invio) restano gestite dal sistema.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {Object.entries(TEMPLATE_META).map(([k, { label, Icon }]) => (
            <button key={k} data-testid={`config-tpl-tab-${k}`} onClick={() => setKind(k)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold transition-all ${kind === k ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Oggetto</Label>
          <Input data-testid="tpl-oggetto" value={cur.oggetto || ""} onChange={(e) => setField("oggetto", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Titolo / Intestazione</Label>
          <Input data-testid="tpl-titolo" value={cur.titolo || ""} onChange={(e) => setField("titolo", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Testo principale</Label>
          <Textarea data-testid="tpl-testo" rows={6} value={cur.testo || ""} onChange={(e) => setField("testo", e.target.value)} />
          <p className="text-xs text-slate-400">Segnaposto utilizzabili: {"{nome}"}, {"{cognome}"}{kind === "attivazione" ? ", {email}, {profilo}, {appalti}" : ""}. Lascia una riga vuota per separare i paragrafi.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-slate-700">Firma finale</Label>
          <Textarea data-testid="tpl-firma" rows={3} value={cur.firma || ""} onChange={(e) => setField("firma", e.target.value)} />
        </div>

        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3.5 space-y-2" data-testid="tpl-protected">
          <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Elementi tecnici protetti (non modificabili)</p>
          <div className="inline-flex items-center gap-2 rounded-lg bg-primary/90 text-white text-xs font-semibold px-3.5 py-2 opacity-90 cursor-not-allowed">
            <Lock className="h-3.5 w-3.5" />{prot.button_label}
          </div>
          <p className="text-xs text-slate-500 break-all">Link/token: <span className="text-primary">{prot.link_placeholder}</span> · gestito dal sistema</p>
          <p className="text-xs text-slate-500">Scadenza: {prot.ttl_note}</p>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" onClick={doPreview} disabled={previewing} data-testid="tpl-preview-btn" className="rounded-lg font-semibold">
            {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}Anteprima
          </Button>
          <Button onClick={save} disabled={saving} data-testid="tpl-save-btn" className="rounded-lg font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Salva
          </Button>
        </div>
      </CardContent>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="tpl-preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima email</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600"><span className="font-semibold">Oggetto:</span> {preview.subject}</p>
              <iframe title="anteprima-email" data-testid="tpl-preview-frame" srcDoc={preview.html}
                className="w-full h-[60vh] rounded-lg border border-slate-200 bg-white" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function ConfigurazioniSection() {
  return (
    <div className="space-y-4" data-testid="config-section">
      <ReferenceEmailsCard />
      <TemplatesCard />
    </div>
  );
}
